import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Navbar } from "@/components/site/Navbar";
import { scrollToSelector } from "@/hooks/useLenis";
import { Hero } from "@/components/site/Hero";
import { WhatsAppJoin } from "@/components/site/WhatsAppJoin";
import { Categories } from "@/components/site/Categories";
import { HolidayStrip } from "@/components/site/HolidayStrip";
import { StatsBoard } from "@/components/site/StatsBoard";
import { Events } from "@/components/site/Events";
import { Donate } from "@/components/site/Donate";
import { Gallery } from "@/components/site/Gallery";
import { Contact } from "@/components/site/Contact";
import { CommunityServices } from "@/components/site/CommunityServices";
import { Footer } from "@/components/site/Footer";
import { useEditMode } from "@/context/EditModeContext";
import { SectionShell } from "@/components/site/edit/SectionShell";

const SECTIONS = {
  hero: { Comp: Hero, label: "Hero" },
  categories: { Comp: Categories, label: "Programs" },
  whatsapp: { Comp: WhatsAppJoin, label: "WhatsApp strip" },
  stats: { Comp: StatsBoard, label: "Numbers" },
  events: { Comp: Events, label: "Events" },
  holidays: { Comp: HolidayStrip, label: "Holidays strip" },
  gallery: { Comp: Gallery, label: "Gallery" },
  services: { Comp: CommunityServices, label: "Services" },
  donate: { Comp: Donate, label: "Donate" },
};
const DEFAULT_ORDER = ["hero", "categories", "whatsapp", "stats", "events", "holidays", "gallery", "services", "donate"];

export default function Home() {
  const location = useLocation();
  const em = useEditMode();
  const order = em.getOrder(DEFAULT_ORDER);

  useEffect(() => {
    if (!location.hash) return;
    let cancelled = false;
    const timers = [];
    // The moment the user scrolls/interacts, abort any pending auto-scroll so
    // we never fight their input (was causing a scroll snap-back).
    const abort = () => {
      cancelled = true;
      timers.forEach((id) => clearTimeout(id));
      remove();
    };
    const remove = () => {
      window.removeEventListener("wheel", abort);
      window.removeEventListener("touchmove", abort);
      window.removeEventListener("keydown", abort);
    };
    window.addEventListener("wheel", abort, { passive: true });
    window.addEventListener("touchmove", abort, { passive: true });
    window.addEventListener("keydown", abort);

    const scroll = () => {
      if (cancelled) return;
      scrollToSelector(location.hash);
      // One gentle correction after heavy images settle (cold-cache layout shift).
      timers.push(setTimeout(() => { if (!cancelled) scrollToSelector(location.hash); }, 1200));
    };

    const pending = Array.from(document.images).filter((img) => !img.complete);
    if (pending.length === 0) {
      timers.push(setTimeout(scroll, 250));
    } else {
      let remaining = pending.length;
      const onDone = () => { remaining -= 1; if (remaining <= 0 && !cancelled) scroll(); };
      pending.forEach((img) => {
        img.addEventListener("load", onDone);
        img.addEventListener("error", onDone);
      });
      timers.push(setTimeout(() => { if (!cancelled) scroll(); }, 1800));
    }
    return () => {
      cancelled = true;
      timers.forEach((id) => clearTimeout(id));
      remove();
    };
  }, [location]);

  return (
    <div className="relative bg-[#F9F8F6] overflow-x-hidden">
      <div className="grain" />
      <Navbar />
      <main>
        {order.map((id, idx) => {
          const S = SECTIONS[id];
          if (!S) return null;
          const Comp = S.Comp;
          return (
            <SectionShell key={id} id={id} label={S.label} order={order} isFirst={idx === 0} isLast={idx === order.length - 1}>
              <Comp />
            </SectionShell>
          );
        })}
      </main>
      <Contact />
      <Footer />
    </div>
  );
}
